/**
 * LockScene.tsx — Composition root of the constrained 3D configurator (T15).
 *
 * ROOT of rebuild `web/src/3d_new/`: mount react-three-fiber `<Canvas>`
 * with configurable camera and lights (LockScene.types.ts) and renders the scene
 * resolved. It's the ONLY place where <Canvas> and render components come from
 * compounds.
 *
 * Flow (PLAN §1.1, §2.20):
 *
 * ```
 * LockScene (public props from LockScene.types.ts)
 * │
 * ├─ useLockScene() ← OUTSIDE <Canvas>: orchestration in the React tree
 * │ of the FormProvider (form → features → assets → resolveScene, T13)
 * │ React contexts (react-hook-form) DO NOT traverse r3f's <Canvas>,
 * │ then the resolved scene passes as PROPS to SceneContent.
 * │
 * └─ <Canvas> (camera + lights + Suspense + OrbitControls)
 * └─ <SceneContent scene assets hasData props>
 * ├─ SimpleGrid (X-Z plane at y=0, config GRID_DEFAULTS)
 * ├─ Beam header (COLORS.headerBeam)
 * ├─ Beam joist (COLORS.joistBeam)
 * ├─ ConnectorJoist (for each joist side instance)
 * ├─ ConnectorHeader (for each header side instance)
 * ├─ Fastener (for each FastenerAnchor)
 * └─ ConstraintDebug (if showDebug)
 * ```
 *
 * componentsState (LockSceneComponentsState):
 * - visible → filter the group (visible prop)
 * - opacity → overlay on materials (prop opacity)
 * - explodeFactor → vista esplosa lungo world X (corretta 30-09-2026, layout
 * in `model/explode.ts`): trave header | lock header | vite header | gap |
 * vite joist | lock joist | trave joist, con uno spazio tra tutti gli
 * elementi; con lockstop: lock | gap | lockstop | gap | viti. Viti e
 * lockstop seguono il proprio lock; la linea tratteggiata (`ExplodeGuideLine`)
 * va dal foro del lock ESPLOSO alla vite, attraversando il lockstop.
 * - lockstop → not implemented in v1 (out of scope, SPEC §8)
 *
 * No calculation logic here: the scene ALREADY arrives resolved by useLockScene
 * (AC-7: components/ MUTO; this is orchestration, not geometry).
 *
 * Constrained 3D configurator: each block is commented for maintainability.*/

import { Canvas, type RootState, useThree } from "@react-three/fiber";
import { Environment, OrbitControls, OrthographicCamera } from "@react-three/drei";
import { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

import { COLORS } from "./config/colors";
import { BEAM_OPACITY, EXPLODE_DEFAULTS, FOCUS_FRAMING, GRID_DEFAULTS } from "./config/defaults";
import { useLockScene } from "./hooks/useLockScene";
import type { LockAssets } from "./hooks/useAssets";
import type { BeamSpec, ResolvedScene } from "./model/types";
import type { LockstopInstance } from "./model/lockstopPlacement";
import { connectorsFocus } from "./model/connectors";
import { fastenerColorFor } from "./model/fasteners";
import { beamOwnerId, connectorOwnerId, PER_VIEW_COPY_QUOTE_IDS } from "./model/quotes";
import type { Vec3 } from "./data/types";
import type { LockSceneProps, LightingConfig, ViewPresetRequest, CameraPreset } from "./LockScene.types";
import { Beam } from "./components/beams/Beam";
import { ConnectorHeader } from "./components/locks/ConnectorHeader";
import { ConnectorJoist } from "./components/locks/ConnectorJoist";
import { applyConnectorMeshOffset, ConnectorMeshOffset } from "./components/locks/ConnectorMeshOffset";
import { MeasureConnectorFlushThickness } from "./components/locks/MeasureConnectorFlushThickness";
import { GlbView } from "./components/locks/GlbView";
import { Fastener } from "./components/fasteners/Fastener";
import { ExplodeGuideLine } from "./components/fasteners/ExplodeGuideLine";
import { SimpleGrid } from "./components/grid/SimpleGrid";
import { ConstraintDebug } from "./components/debug/ConstraintDebug";
import { isDebugToolsEnabled } from "../../../../utils/environment";
import { registerCapturableCanvas } from "../../../../utils/webglCanvases";
import { Quotes } from "./components/quotes/Quotes";
import { ForceArrows } from "./components/forces/ForceArrows";
import { computeAssemblyMidpoint } from "./model/forces";
import { explodedFastenerPoints, explodeLayoutForScene } from "./model/explode";

//─── Camera / light defaults (AC-1 visual calibration points) ─

const DEFAULT_CAMERA_POSITION: [number, number, number] = [800, 600, 800];
const DEFAULT_CAMERA_CONFIG = { fov: 40, near: 1, far: 10000 };

// In ortografica il near non ha effetto sulla scala: tenerlo basso evita solo
// di tagliare la scena quando la camera le sta molto vicina.
const ORTHO_NEAR = 0.1;

// Direzioni di vista (versori): la distanza non e' piu' fissa, si ricava dal
// raggio dei connettori, cosi' ogni preset li inquadra invece dell'assieme.
const PRESET_DIRECTIONS: Record<CameraPreset, THREE.Vector3Tuple> = {
  front:       [0, 0, 1],
  top:         [0, 1, 0],
  side:        [1, 0, 0],
  perspective: [0.6247, 0.4685, 0.6247],
};
const DEFAULT_LIGHTING: LightingConfig = {
  ambientIntensity: 0.6,
  mainLight: { position: [5, 10, 5], intensity: 1.2, castShadow: true },
  fillLight: { position: [-3, 5, -3], intensity: 0.4 },
};

// ─── LockScene (root) ─────────────────────────────────────────

/**
 * Root of the 3D configurator. Orchestration OUTSIDE the Canvas (useLockScene),
 * render inside the Canvas (SceneContent).*/
const LockScene: FC<LockSceneProps> = ({
  cameraConfig,
  lightingConfig,
  componentsState,
  viewPreset,
  input,
  showDebug: requestedDebug = false,
  userQuotes,
  serviceClass,
  preDrillingLines,
  calculationLegend,
  loads,
  lockstopInstallationLevels,
  lockstopCode,
  screwEdgeThicknessMm,
  className,
  onLoaded,
}) => {
  // Debug helpers never run in production, whatever the caller asks for.
  const showDebug = requestedDebug && isDebugToolsEnabled();
  //Orchestration in the FormProvider's React tree (NOT inside <Canvas>).
  const { scene, assets, lockstops, hasData } = useLockScene(input, {
    installationLevels: lockstopInstallationLevels ?? null,
    code: lockstopCode ?? null,
    screwEdgeThicknessMm: screwEdgeThicknessMm ?? null,
  });

  //Callback onLoaded: scene resolved → notify the consumer (assets ready).
  useEffect(() => {
    if (hasData) onLoaded?.();
  }, [hasData, onLoaded]);

  // Inquadratura sui soli connettori (model/): le travi restano contesto ma
  // non decidono lo zoom. Stessa regola usata dalla capture del report.
  const focus = useMemo(() => connectorsFocus(scene?.connectors ?? []), [scene]);

  // Estensione massima delle travi (bug corretto 24-09-2026: la vista "side"
  // guarda lungo la fibra della joist, e con la sola distanza radius*4 la sua
  // estremita' lontana finiva dietro la camera). Non cambia lo zoom (che resta
  // sui soli connettori), solo la distanza minima della camera ortografica.
  //
  // Seconda correzione (30-09-2026, "la vista viene tagliata"): OrbitControls
  // ruota la camera attorno al target mantenendo la distanza, quindi la scena
  // deve stare davanti alla camera in QUALUNQUE direzione, non solo nei preset.
  // Serve la portata massima dal connettore: la joist (e la colonna) crescono
  // per TUTTA la lunghezza dal connettore, non meta' — si usa la diagonale
  // intera l×h×b della trave piu' grande, per eccesso.
  const sceneReachMm = useMemo(() => {
    const diagonal = (dims?: { l: number; h: number; b: number }) => (dims ? Math.hypot(dims.l, dims.h, dims.b) : 0);
    return Math.max(diagonal(scene?.beams.header.dims), diagonal(scene?.beams.joist.dims));
  }, [scene]);

  // Camera: defaults ragionevoli, sovrascrivibili da props (R8 additive).
  const camera = useMemo(
    () => ({
      position: cameraConfig?.position ?? DEFAULT_CAMERA_POSITION,
      fov: cameraConfig?.fov ?? DEFAULT_CAMERA_CONFIG.fov,
      near: cameraConfig?.near ?? DEFAULT_CAMERA_CONFIG.near,
      far: cameraConfig?.far ?? DEFAULT_CAMERA_CONFIG.far,
    }),
    [cameraConfig],
  );

  // Luci: defaults config, sovrascrivibili da props (additive).
  const lighting = useMemo<Required<Pick<LightingConfig, "ambientIntensity" | "mainLight" | "fillLight">>>(
    () => ({
      ambientIntensity: lightingConfig?.ambientIntensity ?? DEFAULT_LIGHTING.ambientIntensity!,
      mainLight: lightingConfig?.mainLight ?? DEFAULT_LIGHTING.mainLight!,
      fillLight: lightingConfig?.fillLight ?? DEFAULT_LIGHTING.fillLight!,
    }),
    [lightingConfig],
  );

  // Diagnostica del contesto WebGL: preventDefault sul "lost" abilita il
  // "restored" (altrimenti il browser non lo emette), cosi' un eventuale
  // ripristino e' osservabile in console invece di lasciare la scena nera.
  const handleCreated = useCallback((state: RootState) => {
    registerCapturableCanvas(state);
    const canvas = state.gl.domElement;
    canvas.addEventListener("webglcontextlost", (event) => {
      event.preventDefault();
      // eslint-disable-next-line no-console
      console.warn("[LockScene] WebGL context lost", event);
    });
    canvas.addEventListener("webglcontextrestored", () => {
      // eslint-disable-next-line no-console
      console.info("[LockScene] WebGL context restored");
    });
  }, []);

  return (
    <div className={className} style={{ width: "100%", height: "100%", position: "relative" }}>
      {/* frameloop="demand": R3F renderizza solo quando la scena cambia (React,
          OrbitControls, testo troika). Con "always" il loop girava a 60 fps anche
          fermo (~77% del main thread nel profilo del 01-10-2026). Le modifiche
          imperative alla camera chiamano invalidate() (CameraPresetController). */}
      <Canvas
        frameloop="demand"
        shadows="percentage"
        camera={camera}
        gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping, toneMappingExposure: 1.1 }}
        style={{ background: COLORS.background }}
        onCreated={handleCreated}
      >
        {/*── Camera attiva: makeDefault la registra su useThree e OrbitControls ──
        SOLO ortografica (29-09-2026, richiesta utente: "dobbiamo utilizzare
        solo OrthographicCamera") — prima si alternava con la prospettica a
        seconda del preset (vedi `CameraPresetController` per il calcolo di
        zoom/distanza, gia' scritto per l'ortografica).*/}
        <OrthographicCamera makeDefault near={ORTHO_NEAR} far={camera.far} />

        {/*Scene Lights (config)*/}
        <ambientLight intensity={lighting.ambientIntensity} />
        <directionalLight
          position={lighting.mainLight.position}
          intensity={lighting.mainLight.intensity}
          castShadow={lighting.mainLight.castShadow}
        />
        <directionalLight
          position={lighting.fillLight.position}
          intensity={lighting.fillLight.intensity}
          castShadow={lighting.fillLight.castShadow}
        />

        {/* Ambiente (optional, da props) */}
        {lightingConfig?.environmentPreset ? (
          <Environment preset={lightingConfig.environmentPreset} />
        ) : null}

        {/*Suspense: Covers GLB loading (empty scene fallback).*/}
        <Suspense fallback={null}>
          <SceneContent
            scene={scene}
            assets={assets}
            lockstops={lockstops}
            hasData={hasData}
            showDebug={showDebug}
            componentsState={componentsState}
            userQuotes={userQuotes}
            loads={loads}
          />
        </Suspense>
        <CameraPresetController viewPreset={viewPreset} focus={focus} sceneReachMm={sceneReachMm} />
      </Canvas>

      <SceneLegend
        connectors={scene?.connectorLegend ?? []}
        headerLockCode={scene?.headerLockCode ?? null}
        fasteners={scene?.fastenerLegend ?? []}
        serviceClass={serviceClass}
        preDrillingLines={preDrillingLines}
        calculationLegend={calculationLegend}
        lockstopCode={lockstopCode}
      />
    </div>
  );
};

export default LockScene;

//─── SceneLegend (overlay HTML, outside the Canvas) ────────────

/**
 * Legenda sovrapposta al canvas: classe di servizio (28-09-2026, numero +
 * descrizione — coerente con l'ambiente/sfondo scelto in LockScene), norma e coefficienti, sopra i
 * codici connettore (senza colore proprio — si distinguono per posizione,
 * non per tinta), le righe del preforo (testo gia' tradotto) e i colori delle
 * viti. Sta qui e non nel viewer perche' LockScene e' l'unico punto che ha la
 * scena risolta. Compatta di default (solo codici prodotto: connettori, lock header, lockstop e viti), si espande dal pulsante.*/
function SceneLegend({
  connectors,
  headerLockCode,
  fasteners,
  serviceClass,
  preDrillingLines,
  calculationLegend,
  lockstopCode,
}: {
  connectors: ResolvedScene["connectorLegend"];
  headerLockCode: ResolvedScene["headerLockCode"];
  fasteners: ResolvedScene["fastenerLegend"];
  serviceClass?: LockSceneProps["serviceClass"];
  preDrillingLines?: LockSceneProps["preDrillingLines"];
  calculationLegend?: LockSceneProps["calculationLegend"];
  lockstopCode?: LockSceneProps["lockstopCode"];
}) {
  const connectorEntries = connectors.filter((c) => c.code);
  const preDrilling = preDrillingLines ?? [];
  const legend = calculationLegend ?? { standard: null, loadDuration: null, coefficients: [], timber: [], combined: null };
  const hasCalculationLegend =
    !!legend.standard || !!legend.loadDuration || legend.coefficients.length > 0 || legend.timber.length > 0 || !!legend.combined;
  //Con un solo prodotto vite i colori non distinguono nulla: quella parte non serve.
  const showFasteners = fasteners.length >= 2;
  // Compatta di default: restano i codici prodotto (connettori, lock header, lockstop e viti); il resto si apre dal pulsante.
  const [expanded, setExpanded] = useState(false);
  const hasDetails = !!serviceClass || hasCalculationLegend || preDrilling.length > 0;
  if (connectorEntries.length === 0 && !headerLockCode && !lockstopCode && !showFasteners && !hasDetails) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: 12,
        bottom: 12,
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: hasDetails ? "8px 26px 8px 10px" : "8px 10px",
        borderRadius: 8,
        background: "rgba(15, 23, 42, 0.72)",
        color: "#e2e8f0",
        fontSize: 11,
        lineHeight: 1.3,
        pointerEvents: "none",
      }}
    >
      {hasDetails ? (
        <button
          type="button"
          aria-expanded={expanded}
          title={expanded ? "Hide legend details" : "Show legend details"}
          onClick={() => setExpanded((open) => !open)}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            padding: 0,
            border: "none",
            background: "transparent",
            color: "inherit",
            fontSize: 11,
            lineHeight: 1,
            cursor: "pointer",
            pointerEvents: "auto",
          }}
        >
          {expanded ? "▾" : "▸"}
        </button>
      ) : null}
      {expanded && legend.standard ? <LegendRow text={legend.standard} /> : null}
      {expanded && serviceClass ? <LegendRow text={`${serviceClass.label} ${serviceClass.classNumber}`} /> : null}
      {expanded && legend.loadDuration ? <LegendRow text={legend.loadDuration} /> : null}
      {expanded &&
        legend.coefficients.map((line) => (
          <LegendRow key={line} text={line} />
        ))}
      {expanded &&
        legend.timber.map((line) => (
          <LegendRow key={line} text={line} />
        ))}
      {expanded && legend.combined ? <LegendRow text={legend.combined} /> : null}
      {connectorEntries.map((c) => (
        <LegendRow key={`connector-${c.index}`} text={`Connector ${c.index + 1}: ${c.code}`} />
      ))}
      {headerLockCode ? <LegendRow text={`Header lock: ${headerLockCode}`} /> : null}
      {lockstopCode ? <LegendRow text={`Lockstop: ${lockstopCode}`} /> : null}
      {expanded &&
        preDrilling.map((line) => (
          <LegendRow key={line} text={line} />
        ))}
      {showFasteners &&
        fasteners.map((entry) => (
          <div key={entry.itemId} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: entry.color,
                flexShrink: 0,
              }}
            />
            <span>{entry.code ?? entry.itemId}</span>
          </div>
        ))}
    </div>
  );
}

/** Una riga di testo della legenda. */
function LegendRow({ text }: { text: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span>{text}</span>
    </div>
  );
}

//─── Vista esplosa: offset travi (render layer, richiesta utente 24-09-2026) ─

/**
 * Copia `spec` con il centro spostato di `offsetXMm` lungo world X (la
 * normale del piano connettore, SPEC G1) — stesso asse su cui si separano
 * gia' i connettori. Puro: non muta `spec`, `model/` non cambia posizione
 * (SPEC §6, AC-7) — la composizione dell'esploso avviene SOLO qui, come per
 * connettori e viti piu' sotto.*/
function explodedBeamSpec(spec: BeamSpec, offsetXMm: number): BeamSpec {
  if (offsetXMm === 0) return spec;
  return { ...spec, center: [spec.center[0] + offsetXMm, spec.center[1], spec.center[2]] };
}

//─── SceneContent (inside the Canvas) ──────────────────────────

interface SceneContentProps {
  scene: ResolvedScene | null;
  assets: LockAssets;
  /** Istanze fisiche del lockstop, gia' risolte (28-09-2026) — vedi useLockScene. */
  lockstops: LockstopInstance[];
  hasData: boolean;
  showDebug: boolean;
  componentsState?: LockSceneProps["componentsState"];
  userQuotes?: LockSceneProps["userQuotes"];
  loads?: LockSceneProps["loads"];
}

/**
 * Scene contents: receives the ALREADY resolved scene and assets as props
 * (React contexts do not cross Canvas r3f). Render Grid,
 * beams, connectors, fasteners and debug — no calculation logic.*/
function SceneContent({
  scene,
  lockstops,
  hasData,
  showDebug,
  componentsState,
  userQuotes,
  loads,
}: SceneContentProps) {
  // Target dell'orbita: il centro dei connettori, non un punto fisso del mondo
  // — altrimenti ruotando si torna a inquadrare l'assieme.
  const controlsTarget = useMemo<[number, number, number]>(
    () => connectorsFocus(scene?.connectors ?? []).center,
    [scene],
  );

  // Centro reale dell'assieme lock (28-09-2026, richiesta utente: le frecce
  // dei carichi vanno "a meta' dell'assieme", non sul riferimento Y del
  // piano connettore — vedi doc-comment su computeAssemblyMidpoint).
  const forceAnchor = useMemo(
    () => computeAssemblyMidpoint((scene?.connectors ?? []).map((c) => c.position)),
    [scene],
  );

  //componentState per gruppo (R8, overlay semplice): travi e lock hanno voci
  //separate, cosi' si puo' togliere una trave e tenere il suo connettore.
  const headerBeamState = componentsState?.headerBeam;
  const joistBeamState = componentsState?.joistBeam;
  const headerLockState = componentsState?.headerLock;
  const joistLockState = componentsState?.joistLock;
  const fastenerState = componentsState?.fasteners;
  const explodeFactor = componentsState?.explodeFactor ?? 0;

  // Layout esploso per lato (model/explode.ts). Lo spessore del lockstop e'
  // quello MISURATO sulla mesh (noto solo dentro il render-prop piu' sotto),
  // per questo si espone una funzione invece del layout gia' calcolato.
  const resolveExplodeLayout = useCallback(
    (lockstopThicknessMm: number) =>
      explodeLayoutForScene(
        { fasteners: scene?.fasteners ?? [], connectors: scene?.connectors ?? [] },
        lockstops,
        lockstopThicknessMm,
        EXPLODE_DEFAULTS.gapMm,
        explodeFactor,
      ),
    [scene, lockstops, explodeFactor],
  );

  // Visibilita' delle quote PER elemento proprietario (ownerId costruiti con
  // le stesse funzioni di model/quotes.ts, SSOT della convenzione): una
  // trave/lock nascosto porta via anche le sue quote, senza serve nuovo
  // stato — riusa gli stessi booleani di beam/lock.
  const quotesVisibility = useMemo(() => {
    const map: Record<string, boolean> = {
      [beamOwnerId("header")]: headerBeamState?.visible ?? true,
      [beamOwnerId("joist")]: joistBeamState?.visible ?? true,
    };
    for (const instance of scene?.connectors ?? []) {
      const state = instance.side === "joist" ? joistLockState : headerLockState;
      map[connectorOwnerId(instance.index, instance.side)] = state?.visible ?? true;
    }
    return map;
  }, [scene, headerBeamState, joistBeamState, joistLockState, headerLockState]);

  return (
    <group>
      {/*── Reference grid (X-Z plane at y=0, AC-1) ──*/}
      {componentsState?.grid?.visible ?? true ? (
        <SimpleGrid
          size={GRID_DEFAULTS.size}
          divisions={GRID_DEFAULTS.divisions}
          color={COLORS.grid}
          floorColor={COLORS.gridFloor}
          borderColor={COLORS.gridBorder}
        />
      ) : null}

      {/*── Beams (BeamSpec ALREADY resolved) ──*/}
      {hasData && scene ? (
        <>
          {/*── Correzione robusta bbox reale (25-09-2026, indagine incastro) ──
          La mesh GLB reale non e' simmetrica sulla propria origine: le
          formule di `connectorPlacement.ts` assumono un modulo centrato
          (come il box fallback). Si calcola qui il centro bbox REALE (max 2
          asset distinti, uno per indice connettore, joist e header dello
          stesso indice condividono lo stesso GLB) e si applica a mesh E viti
          — mai solo alla mesh (vedi `ConnectorMeshOffset.tsx` per il
          perche', e la cronologia dei bug quando veniva fatto altrove).*/}
          <ConnectorMeshOffset url={scene.connectors.find((c) => c.index === 0)?.glbUrl ?? null}>
            {(offset0) => (
              <ConnectorMeshOffset url={scene.connectors.find((c) => c.index === 1)?.glbUrl ?? null}>
                {(offset1) => {
                  const rawCenterByIndex: Record<number, THREE.Vector3> = { 0: offset0, 1: offset1 };
                  // Lockstop coperture per lato+indice connettore (29-09-2026,
                  // richiesta utente: "bisogna allineare le viti nel caso ci
                  // sia un lockstop"): la vite in un foro coperto deve
                  // attraversare anche lo spessore del lockstop, quindi la
                  // sua testa va spostata piu' in fuori (verso -axis, il
                  // verso opposto all'inserimento) di `thicknessMm`. Union
                  // dei pezzi (kind="narrow" ne genera 2 per stesso lato).
                  const lockstopCoverageByOwner = new Map<string, { thicknessMm: number; holeIds: Set<string> }>();
                  for (const lockstop of lockstops) {
                    // Con 2 connettori un pezzo puo' coprire fori di entrambi: la chiave e' del connettore del foro.
                    for (const { connectorIndex, holeId } of lockstop.coveredHoles) {
                      const key = `${lockstop.side}:${connectorIndex}`;
                      const entry = lockstopCoverageByOwner.get(key) ?? {
                        thicknessMm: lockstop.thicknessMm,
                        holeIds: new Set<string>(),
                      };
                      entry.holeIds.add(holeId);
                      lockstopCoverageByOwner.set(key, entry);
                    }
                  }
                  // Spessore REALE del connettore, misurato sulla mesh
                  // (29-09-2026, richiesta utente: "la vite dovrebbe
                  // considerare lo spessore del lock + lo spessore del
                  // lockstop" — misurato, non da catalogo/JSON, per restare
                  // coerente con la faccia dove il lockstop si appoggia
                  // davvero, vedi doc-comment su MeasureConnectorFlushThickness).
                  // Una sola misura per INDICE connettore (joist e header
                  // condividono lo stesso GLB/features, quindi la stessa
                  // faccia) — riusata sia per il lockstop sia per le viti.
                  const probeForIndex = (index: number) => {
                    const owner = scene.connectors.find((c) => c.index === index);
                    const hole = owner?.features.holes.find((h) => h.type === "standard");
                    return {
                      url: owner?.glbUrl ?? null,
                      probeXY: hole ? ([hole.position[0], hole.position[1]] as [number, number]) : null,
                    };
                  };
                  const probe0 = probeForIndex(0);
                  const probe1 = probeForIndex(1);
                  // Spessore REALE del lockstop, misurato sulla mesh VICINO A
                  // UN FORO (29-09-2026, richiesta utente: "mi interessa lo
                  // spessore vicino ai fori e basta") — non il bbox intero:
                  // verificato su LOCKSTOP100 che vicino ai fori e' 2.50mm
                  // mentre il bbox arriva a 15.5mm (bordi/alette rialzate
                  // lontano dai fori). Un solo pezzo basta: tutti quelli dello
                  // stesso lockstop condividono lo stesso GLB/quota.
                  const lockstopProbe = {
                    url: lockstops[0]?.glbUrl ?? null,
                    probeXY: lockstops[0]?.holesXY[0] ?? null,
                  };
                  return (
                    <MeasureConnectorFlushThickness url={probe0.url} probeXY={probe0.probeXY}>
                      {(measured0) => (
                        <MeasureConnectorFlushThickness url={probe1.url} probeXY={probe1.probeXY}>
                          {(measured1) => (
                            <MeasureConnectorFlushThickness url={lockstopProbe.url} probeXY={lockstopProbe.probeXY}>
                              {(measuredLockstopThicknessMm) => {
                            const measuredThicknessByIndex: Record<number, number | null> = {
                              0: measured0,
                              1: measured1,
                            };
                            // Spessore lockstop per l'esploso: misurato vicino
                            // ai fori, fallback sul dichiarato (stessa regola
                            // delle viti coperte piu' sotto).
                            const explodeLayout = resolveExplodeLayout(
                              lockstops[0] ? measuredLockstopThicknessMm ?? lockstops[0].thicknessMm : 0,
                            );
                            return (
                    <>
                      {/*── Beams (BeamSpec ALREADY resolved) — dentro il
                      render-prop perche' il loro offset esploso dipende dallo
                      spessore misurato del lockstop. Le travi sono l'ESTERNO
                      dell'assieme e si allontanano lungo world X dal proprio
                      lato (header −X, joist +X — vedi model/explode.ts).
                      `spec` resta immutato: si compone un nuovo oggetto SOLO
                      qui nel render layer, `Beam.tsx` resta MUTO. ──*/}
                      <Beam
                        spec={explodedBeamSpec(scene.beams.header, explodeLayout.header.beamOffsetXMm)}
                        color={COLORS.headerBeam}
                        showDebug={showDebug}
                        opacity={headerBeamState?.opacity ?? BEAM_OPACITY}
                        visible={headerBeamState?.visible ?? true}
                      />
                      <Beam
                        spec={explodedBeamSpec(scene.beams.joist, explodeLayout.joist.beamOffsetXMm)}
                        color={COLORS.joistBeam}
                        showDebug={showDebug}
                        opacity={joistBeamState?.opacity ?? BEAM_OPACITY}
                        visible={joistBeamState?.visible ?? true}
                      />

                      {/*── Connectors (instances ALREADY placed, a joist+header pair) ──*/}
                      {scene.connectors.map((instance) => {
                        const isJoist = instance.side === "joist";
                        const state = isJoist ? joistLockState : headerLockState;
                        // Vista esplosa: il lock si allontana lungo world X verso
                        //il proprio lato (header −X, joist +X), meno della trave —
                        //vedi model/explode.ts. Composto SOLO qui nel render layer.
                        const renderPosition: [number, number, number] = [
                          instance.position[0] + explodeLayout[instance.side].connectorOffsetXMm,
                          instance.position[1],
                          instance.position[2],
                        ];
                        const correctedPosition = applyConnectorMeshOffset(
                          renderPosition,
                          rawCenterByIndex[instance.index] ?? offset0,
                          instance.quaternion,
                          instance.meshQuaternion,
                        );

                        const common = { showDebug, opacity: state?.opacity, visible: state?.visible ?? true };
                        return isJoist ? (
                          <ConnectorJoist
                            key={`joist-${instance.index}`}
                            instance={instance}
                            position={correctedPosition}
                            color={COLORS.connectorJoist}
                            {...common}
                          />
                        ) : (
                          <ConnectorHeader
                            key={`header-${instance.index}`}
                            instance={instance}
                            position={correctedPosition}
                            color={COLORS.connectorHeader}
                            {...common}
                          />
                        );
                      })}

                      {/*── Fasteners (FastenerAnchor ALREADY solved) ──*/}
                      {scene.fasteners.map((anchor) => {
                        //Colore PER PRODOTTO (item id), non per ruolo: due lati con la
                        //stessa vite hanno lo stesso colore, viti diverse colori diversi.
                        const color = fastenerColorFor(scene.fastenerLegend, anchor.fastenerItemId);
                        const isInclined = anchor.holeType === "inclined";
                        // Lockstop presente su questo foro: la vite attraversa
                        // anche lo spessore della piastra, quindi la testa si
                        // sposta ANCHE di quello spessore, stesso verso — MAI
                        // per le inclinate (il lockstop copre solo fori
                        // standard, vedi `lockstopPlacement.ts`).
                        const coverage = isInclined
                          ? undefined
                          : lockstopCoverageByOwner.get(`${anchor.side}:${anchor.connectorIndex}`);
                        const isCovered = !!coverage?.holeIds.has(anchor.holeId);
                        // Spessore lockstop: quello MISURATO vicino ai fori
                        // (29-09-2026, richiesta utente: "mi interessa lo
                        // spessore vicino ai fori e basta") — non
                        // `coverage.thicknessMm` (il `thickness_mm` dichiarato
                        // nel JSON, che descrive il bordo/aletta rialzata
                        // della piastra, non la zona piatta attorno al foro
                        // dove passa la vite: 15.5mm dichiarato contro 2.50mm
                        // misurato su LOCKSTOP100 — scarto enorme, causa dello
                        // spazio vuoto/gambo visibile segnalato dall'utente).
                        // Fallback sul dichiarato solo se la misura non
                        // risolve.
                        const lockstopThicknessMm = isCovered
                          ? measuredLockstopThicknessMm ?? coverage!.thicknessMm
                          : 0;
                        // NIENTE correzione della faccia connettore qui
                        // (29-09-2026 — provata e tolta lo stesso giorno):
                        // le viti SENZA lockstop, che usano `anchor.position`
                        // cosi' com'e' (quota JSON dichiarata, MAI corretta
                        // con la mesh), risultano gia' visivamente corrette
                        // (testa sporgente) — la convenzione di
                        // `holeToFastenerAnchor` e' quindi calibrata sul
                        // valore dichiarato, non sulla mesh vera. Sottrarre
                        // anche il delta mesh del connettore (come tentato
                        // prima) faceva rientrare la testa delle viti coperte
                        // troppo, appiattendola sul lockstop invece di
                        // farla sporgere (segnalato dall'utente con
                        // screenshot). Si aggiunge quindi SOLO lo spessore
                        // del lockstop (misurato) sopra alla stessa base
                        // dichiarata gia' usata per le viti non coperte.
                        const totalShiftMm = lockstopThicknessMm;
                        const anchorPosition: [number, number, number] = [
                          anchor.position[0] - anchor.axis[0] * totalShiftMm,
                          anchor.position[1] - anchor.axis[1] * totalShiftMm,
                          anchor.position[2] - anchor.axis[2] * totalShiftMm,
                        ];
                        if (showDebug && !isInclined) {
                          // Solo con il toggle constraint debug: la stringa viene
                          // costruita a ogni render per ogni vite, non in produzione.
                          // Una SOLA riga per vite (29-09-2026): le righe multiple
                          // finivano tagliate dal copia-incolla (probabile filtro
                          // devtools attivo su "copertura" che nascondeva le
                          // righe di dettaglio successive, prive di quel testo).
                          console.log(
                            `[LockScene] vite ${anchor.id} | holeId=${anchor.holeId} side=${anchor.side} idx=${anchor.connectorIndex} | localAnchorZMm=${anchor.localAnchorZMm.toFixed(2)} | coverageHoleIds=[${coverage ? [...coverage.holeIds].join(",") : "nessun lockstop"}] | isCovered=${isCovered} measuredLockstopThicknessMm=${measuredLockstopThicknessMm} lockstopThicknessMm=${lockstopThicknessMm} | totalShiftMm=${totalShiftMm.toFixed(2)} | axis=(${anchor.axis[0].toFixed(3)},${anchor.axis[1].toFixed(3)},${anchor.axis[2].toFixed(3)}) | posPrima=(${anchor.position[0].toFixed(2)},${anchor.position[1].toFixed(2)},${anchor.position[2].toFixed(2)}) | posDopo=(${anchorPosition[0].toFixed(2)},${anchorPosition[1].toFixed(2)},${anchorPosition[2].toFixed(2)})`,
                          );
                        }
                        // Vista esplosa: vite e foro sul lock esploso (model/explode.ts).
                        const { screw: extracted, hole: holeExploded } = explodedFastenerPoints(
                          anchor,
                          anchorPosition,
                          explodeLayout[anchor.side],
                        );
                        //Stessa correzione bbox del connettore proprietario di
                        //questo foro (stesso quaternion, stesso indice) — mesh e
                        //vite restano solidali (SPEC §6, AC-7: nessuna posizione
                        //mutata nel model, tutto composto qui).
                        const owner = scene.connectors.find(
                          (c) => c.side === anchor.side && c.index === anchor.connectorIndex,
                        );
                        const ownerRawCenter = rawCenterByIndex[anchor.connectorIndex] ?? offset0;
                        const correctedExtracted = owner
                          ? applyConnectorMeshOffset(extracted, ownerRawCenter, owner.quaternion, owner.meshQuaternion)
                          : extracted;
                        //Punto di partenza della guida: il foro sul lock GIA'
                        //esploso (non quello assemblato, che resterebbe tra i
                        //lock), con la stessa correzione bbox.
                        const correctedHolePosition = owner
                          ? applyConnectorMeshOffset(holeExploded, ownerRawCenter, owner.quaternion, owner.meshQuaternion)
                          : holeExploded;
                        return (
                          <group key={anchor.id}>
                            <Fastener
                              anchor={anchor}
                              position={correctedExtracted}
                              color={color}
                              showDebug={showDebug}
                              opacity={fastenerState?.opacity}
                              visible={fastenerState?.visible ?? true}
                            />
                            {/*Linea guida tratteggiata foro↔vite (richiesta utente
                            24-09-2026): solo quando l'esploso le ha separate, altrimenti
                            sarebbe un punto invisibile sovrapposto al foro.*/}
                            {explodeFactor > 0 && (fastenerState?.visible ?? true) ? (
                              <ExplodeGuideLine from={correctedHolePosition} to={correctedExtracted} />
                            ) : null}
                          </group>
                        );
                      })}

                      {/*── Lockstop (piastra/gancio di rinforzo, 28-09-2026) —
                      GLB reale se disponibile (R6, stesso pattern dei
                      connettori), altrimenti box parametrico. STESSA
                      correzione bbox (ConnectorMeshOffset) del connettore
                      proprietario di quella riga — senza, la posizione resta
                      quella del "modello prima delle rotazioni/correzioni"
                      (bug segnalato dall'utente 28-09-2026): la mesh
                      renderizzata del connettore si sposta con
                      applyConnectorMeshOffset, il lockstop deve seguirla per
                      restare sui fori reali, non solo su quelli calcolati
                      dalla formula.*/}
                      {(componentsState?.lockstop?.visible ?? true) &&
                        lockstops.map((lockstop) => {
                          const owner = scene.connectors.find(
                            (c) => c.side === lockstop.side && c.index === lockstop.connectorIndex,
                          );
                          const ownerRawCenter = rawCenterByIndex[lockstop.connectorIndex] ?? offset0;
                          const measuredThicknessMm = measuredThicknessByIndex[lockstop.connectorIndex] ?? null;
                          //Z LOCALE misurata sulla mesh vera (29-09-2026,
                          //richiesta utente) al posto del fallback
                          //screwEdgeThicknessMm/hole.position.z: un
                          //delta sulla Z locale sposta `position` di
                          //`delta * asse Z locale portato nel mondo`
                          //(il frame e' pura rotazione+traslazione, vedi
                          //doc-comment su `LockstopInstance.localAnchorZMm`).
                          const zCorrectedPosition: Vec3 =
                            measuredThicknessMm != null
                              ? (() => {
                                  const delta = measuredThicknessMm - lockstop.localAnchorZMm;
                                  const worldZAxis = new THREE.Vector3(0, 0, 1).applyQuaternion(lockstop.quaternion);
                                  return [
                                    lockstop.position[0] + delta * worldZAxis.x,
                                    lockstop.position[1] + delta * worldZAxis.y,
                                    lockstop.position[2] + delta * worldZAxis.z,
                                  ];
                                })()
                              : lockstop.position;
                          //Vista esplosa: il lockstop segue il lock ma se ne
                          //stacca di un gap verso il centro (model/explode.ts).
                          const explodedPosition: Vec3 = [
                            zCorrectedPosition[0] + explodeLayout[lockstop.side].lockstopOffsetXMm,
                            zCorrectedPosition[1],
                            zCorrectedPosition[2],
                          ];
                          const correctedPosition = owner
                            ? applyConnectorMeshOffset(
                                explodedPosition,
                                ownerRawCenter,
                                owner.quaternion,
                                owner.meshQuaternion,
                              )
                            : explodedPosition;

                          return (
                            <group
                              key={lockstop.id}
                              position={correctedPosition}
                              quaternion={lockstop.quaternion}
                            >
                              {lockstop.glbUrl ? (
                                <GlbView url={lockstop.glbUrl} />
                              ) : (
                                <mesh castShadow receiveShadow>
                                  <boxGeometry args={[lockstop.widthMm, lockstop.heightMm, lockstop.thicknessMm]} />
                                  <meshStandardMaterial
                                    color={COLORS.lockstop}
                                    roughness={0.5}
                                    metalness={0.6}
                                    transparent={(componentsState?.lockstop?.opacity ?? 1) < 1}
                                    opacity={componentsState?.lockstop?.opacity ?? 1}
                                  />
                                </mesh>
                              )}
                            </group>
                          );
                        })}
                    </>
                            );
                          }}
                            </MeasureConnectorFlushThickness>
                          )}
                        </MeasureConnectorFlushThickness>
                      )}
                    </MeasureConnectorFlushThickness>
                  );
                }}
              </ConnectorMeshOffset>
            )}
          </ConnectorMeshOffset>

          {/*── Constraint debug helper (if showDebug) ──*/}
          {showDebug ? <ConstraintDebug scene={scene} /> : null}
          {/*── Quote tecniche (toggle "Quotes" del menu Elements, default OFF).
          NESSUN filtro per vista qui (30-09-2026): nel viewer interattivo tutte
          le quote esistono nello spazio 3D a prescindere dalla camera —
          `quote.planes` serve solo all'export PDF (vedi `Quotes.exportPlane`). ──*/}
          {componentsState?.quotes?.visible ?? false ? (
            // Vista 3D libera: senza le copie per vista del report, che qui si
            // vedrebbero doppie (30-09-2026, "le quote si ripetono").
            <Quotes
              scene={scene}
              userQuotes={userQuotes}
              visibility={quotesVisibility}
              excludeIds={PER_VIEW_COPY_QUOTE_IDS}
            />
          ) : null}

          {/*── Frecce dei carichi applicati (toggle "Forces" del menu Elements,
          default OFF — 28-09-2026) — ancorate al CENTRO REALE dell'assieme
          lock (`forceAnchor`), non al riferimento del piano connettore: i
          carichi sono globali per l'assieme, richiesta utente esplicita. ──*/}
          {(componentsState?.forces?.visible ?? false) && loads ? (
            <ForceArrows origin={forceAnchor} loads={loads} />
          ) : null}
        </>
      ) : null}

      {/* ── OrbitControls (camera interattiva) ── */}
      <OrbitControls
        makeDefault
        enableDamping
        dampingFactor={0.15}
        minDistance={100}
        maxDistance={5000}
        target={controlsTarget}
        enablePan
        panSpeed={1.0}
        rotateSpeed={0.8}
      />
    </group>
  );
}

//─── CameraPresetController (inside Canvas) ──────────────────

/**
 * Applies a named camera preset (Front/Top/Side/Perspective) when `viewPreset`
 * changes. Uses `useThree()` to access the camera and the OrbitControls
 * instance registered as default (makeDefault on <OrbitControls>).
 * `seq` lets the toolbar re-apply the same preset consecutively.
 */
/** Distanza minima della camera ortografica dal target perche' tutta la scena le stia davanti. */
function orthoClearanceFor(radiusMm: number, sceneReachMm: number): number {
  return radiusMm + sceneReachMm + FOCUS_FRAMING.orthoClearanceMarginMm;
}

function CameraPresetController({
  viewPreset,
  focus,
  sceneReachMm,
}: {
  viewPreset?: ViewPresetRequest | null;
  /** Centro e raggio dei connettori (model/), su cui si inquadra la camera. */
  focus: { center: Vec3; radius: number };
  /**
   * Portata massima della scena dal connettore (diagonale intera della trave
   * piu' grande — bug corretti 24-09-2026 e 30-09-2026: l'estremita' lontana
   * di una trave finiva dietro la camera, clippata dal near-plane, prima in
   * vista "side" e poi ruotando a mano con OrbitControls). Allarga solo la
   * DISTANZA minima, mai lo zoom.*/
  sceneReachMm: number;
}) {
  const { camera, controls, size, invalidate } = useThree();
  const lastSeq = useRef<number>(-1);
  const lastCamera = useRef<THREE.Camera | null>(null);
  // Livello dell'ultima inquadratura automatica: −1 nessuna, 0 scena vuota,
  // 1 sole travi, 2 connettori (bug 01-10-2026: senza connettori la camera non
  // veniva MAI inquadrata e restava vicino all'origine — near-plane che
  // tagliava travi e griglia al primo caricamento).
  const framedLevel = useRef(-1);

  useEffect(() => {
    // Il remount della camera (es. hot reload, o r3f che la ricrea) monta con
    // lo stesso seq: senza questo controllo resterebbe all'origine, non
    // inquadrata.
    const cameraChanged = lastCamera.current !== camera;
    const isPreset = !!viewPreset && (viewPreset.seq !== lastSeq.current || cameraChanged);
    const hasFocus = focus.radius > 0;
    const level = hasFocus ? 2 : sceneReachMm > 0 ? 1 : 0;
    // Inquadratura automatica SOLO quando la scena sale di livello (subito
    // all'avvio, poi alle travi, poi ai connettori), altrimenti solo sui
    // preset: ogni altro ricalcolo annullerebbe lo zoom fatto a mano.
    if (!isPreset && level <= framedLevel.current) return;
    if (isPreset) lastSeq.current = viewPreset!.seq;
    lastCamera.current = camera;
    framedLevel.current = Math.max(framedLevel.current, level);

    const target = new THREE.Vector3(focus.center[0], focus.center[1], focus.center[2]);
    // Senza connettori si inquadrano le travi (meta' della loro portata),
    // altrimenti i connettori come prima.
    const radius = Math.max(hasFocus ? focus.radius : sceneReachMm / 2, FOCUS_FRAMING.viewer.minRadiusMm);

    // Solo ortografica (29-09-2026): la distanza non scala nulla (lo zoom
    // sotto dipende solo dal raggio) — deve pero' superare la portata reale
    // della scena in QUALUNQUE direzione (l'utente ruota con OrbitControls),
    // altrimenti l'estremita' lontana di una trave finisce DIETRO la camera
    // (clippata dal near-plane). Nessun costo visivo: la distanza non cambia lo zoom.
    const distance = Math.max(radius * 4, orthoClearanceFor(radius, sceneReachMm));

    // Il frustum di default e' in pixel di canvas, quindi lo zoom e' "pixel
    // per mm": ci stanno 2·r·margine millimetri nell'altezza del viewport.
    (camera as THREE.OrthographicCamera).zoom = size.height / (2 * radius * FOCUS_FRAMING.viewer.marginFactor);
    camera.updateProjectionMatrix();

    // Su preset si usa la sua direzione; sulla prima inquadratura si parte
    // sempre dalla vista prospettica 3/4, non dall'angolo grezzo camera-target
    // (con il centro lontano dall'origine si capovolgeva, es. dal basso
    // riaprendo un calcolo).
    const direction = new THREE.Vector3();
    if (isPreset) {
      const [dx, dy, dz] = PRESET_DIRECTIONS[viewPreset!.view];
      direction.set(dx, dy, dz);
    } else {
      direction.set(...PRESET_DIRECTIONS.perspective);
    }
    if (direction.lengthSq() < 1e-9) direction.set(...PRESET_DIRECTIONS.perspective);
    direction.normalize();

    camera.position.copy(target).addScaledVector(direction, distance);
    camera.lookAt(target);

    if (controls) {
      // OrbitControls registered via makeDefault exposes target + update().
      const ctrl = controls as unknown as { target: THREE.Vector3; update(): void };
      ctrl.target.copy(target);
      ctrl.update();
    }
    // Camera mossa a mano: con frameloop="demand" r3f non se ne accorge.
    invalidate();
    // size serve solo a calcolare lo zoom ortografico al momento in cui si
    // applica: ridimensionare la finestra allarga il campo, come una vera
    // ortografica, invece di rifare l'inquadratura sotto le mani dell'utente.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPreset, focus, camera, controls, sceneReachMm]);

  // Scena cresciuta DOPO la prima inquadratura (travi piu' lunghe, header
  // passato a colonna, …): l'effetto sopra non riinquadra per non annullare
  // lo zoom dell'utente, quindi qui si arretra solo la camera lungo la sua
  // direzione attuale se non e' abbastanza lontana. Orientamento e zoom intatti.
  useEffect(() => {
    if (!controls) return;
    const target = (controls as unknown as { target: THREE.Vector3 }).target;
    const radius = Math.max(focus.radius, FOCUS_FRAMING.viewer.minRadiusMm);
    const required = orthoClearanceFor(radius, sceneReachMm);
    const offset = camera.position.clone().sub(target);
    const current = offset.length();
    if (current >= required || current < 1e-6) return;
    camera.position.copy(target).addScaledVector(offset.normalize(), required);
    camera.updateMatrixWorld();
    invalidate();
  }, [sceneReachMm, focus, camera, controls, invalidate]);

  return null;
}
